import React, { Fragment, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';

import MultiSelect from '../../../../../../components/global/MultiSelect';
import Input from '../../../../../../components/global/Input';
import Button from '../../../../../../components/global/Button';
import Label from '../../../../../../components/global/Label';
import useAuthenticated from '../../../../../../hooks/useAuthenticated';
import { MeForAlbumFoldersSelect } from '../../../../../../graphql/queries/account';
import { AlbumFolderForAlbumSelect } from '../../../../../../graphql/queries/album-folder';
import Select from '../../../../../../components/global/Select';
import { ADDRESS_BOOK_CONTACT_TYPE_FILTER } from '../../constants';
import { capitalize } from '../../../../../../util/text';
import useBasename from '../../../../../../components/routing/useBasename';
import { getBasenameDefinitions } from '../../../../../../util/role-definitions';
import { canAccessGroupAndCabinOnBasename } from '../../../../../../util/product-permissions';
import { getAlbumFolderAlbumFlatList } from '../../../../../../util/album-folder';

const propTypes = {
  contactFilter: PropTypes.oneOf(
    Object.values(ADDRESS_BOOK_CONTACT_TYPE_FILTER),
  ),
  setContactFilter: PropTypes.func,
  albumFolderId: PropTypes.string,
  setAlbumFolderId: PropTypes.func,
  albums: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  setAlbums: PropTypes.func,
  cabinName: PropTypes.string,
  groupName: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
};

const AddressBookFilters = ({
  contactFilter,
  setContactFilter,
  albumFolderId,
  setAlbumFolderId,
  albums,
  setAlbums,
  cabinName,
  groupName,
  onConfirm,
  onRequestClose,
}) => {
  const basename = useBasename();
  const authenticated = useAuthenticated();
  const definitions = getBasenameDefinitions(basename);
  const canAccessCabinAndGroup = canAccessGroupAndCabinOnBasename(basename);

  const [localContactFilter, setLocalContactFilter] = useState(
    contactFilter || ADDRESS_BOOK_CONTACT_TYPE_FILTER.CONTACTS_ONLY,
  );
  const [localAlbumFolderId, setLocalAlbumFolderId] = useState(
    albumFolderId || '',
  );
  const [localAlbums, setLocalAlbums] = useState(albums || []);
  const [localCabinName, setLocalCabinName] = useState(cabinName || '');
  const [localGroupName, setLocalGroupName] = useState(groupName || '');

  const { data: { me } = {} } = useQuery(MeForAlbumFoldersSelect, {
    variables: {
      album_type_subtype_web_app_basename_filters:
        definitions.allowedContentTypes,
    },
    skip: !authenticated,
  });
  const { data: { albumFolder } = {} } = useQuery(AlbumFolderForAlbumSelect, {
    variables: {
      id: localAlbumFolderId,
    },
    skip: !localAlbumFolderId,
  });

  const albumOptions = useMemo(() => {
    if (!albumFolder) {
      return [];
    }

    return getAlbumFolderAlbumFlatList(albumFolder, true);
  }, [albumFolder]);

  const handleConfirm = () => {
    if (setContactFilter) setContactFilter(localContactFilter);
    if (setAlbumFolderId) setAlbumFolderId(localAlbumFolderId);
    if (setAlbums) setAlbums(localAlbums);

    onConfirm({
      contactFilter: localContactFilter,
      albumFolderId: localAlbumFolderId,
      albums: localAlbums,
      cabinName: localCabinName,
      groupName: localGroupName,
    });

    onRequestClose();
  };

  return (
    <div className="address-book-filters-modal">
      <div className="modal-content-header">
        <h2>Filter your address book</h2>
      </div>
      <div className="modal-content-body">
        <MultiSelect
          id="album-folder-select"
          name="albumFolderSelect"
          label="Filter by gallery"
          data-testid="album-folder-select"
          placeholder="Select a gallery"
          value={
            albumFolder
              ? { value: albumFolder.id, label: albumFolder.name }
              : null
          }
          options={(me?.album_folders?.records ?? []).map(folder => ({
            label: folder.name,
            value: folder.id,
          }))}
          onChange={option => {
            setLocalAlbumFolderId(option?.value || '');
            setLocalAlbums([]);
            if (!option?.value) {
              setLocalCabinName('');
              setLocalGroupName('');
            }
          }}
          isClearable
        />

        {localAlbumFolderId && (
          <Fragment>
            <MultiSelect
              id="albums-select"
              name="albumsSelect"
              label="Filter by albums (optional)"
              data-testid="albums-select"
              placeholder={
                !albumFolder ? 'Select a gallery first...' : 'Select albums'
              }
              noOptionsMessage={() =>
                !albumFolder
                  ? 'Select a gallery first...'
                  : 'This gallery does not have any albums'
              }
              isDisabled={!albumFolder}
              value={localAlbums}
              options={albumOptions.map(album => ({
                label: album.name,
                value: album.id,
              }))}
              onChange={options => setLocalAlbums(options || [])}
              isMulti
              isClearable
              maxMenuHeight={200}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
              }}
            />

            <Select
              id="contact-filter-select"
              name="contactFilterSelect"
              label="Filter by relationship type"
              labelTip="Filter by users joined to a gallery, or contacts linked to a gallery. This filter will only be applied when filtering by gallery and albums"
              value={localContactFilter}
              onChange={({ target }) => setLocalContactFilter(target.value)}
            >
              {Object.values(ADDRESS_BOOK_CONTACT_TYPE_FILTER).map(value => (
                <option key={value} value={value}>
                  {capitalize(value.toLowerCase().replace('_', ' '))}
                </option>
              ))}
            </Select>

            {canAccessCabinAndGroup && (
              <Fragment>
                <div className="form-group">
                  <Label id="cabin-name-input" label="Filter by cabin" />
                  <Input
                    id="cabin-name-input"
                    name="cabinName"
                    type="text"
                    placeholder="Select..."
                    value={localCabinName}
                    onChange={event => {
                      setLocalCabinName(event.target.value);
                    }}
                  />
                </div>

                <div className="form-group">
                  <Label id="group-name-input" label="Filter by group name" />
                  <Input
                    id="group-name-input"
                    name="groupName"
                    type="text"
                    placeholder="Select..."
                    value={localGroupName}
                    onChange={event => {
                      setLocalGroupName(event.target.value);
                    }}
                  />
                </div>
              </Fragment>
            )}
          </Fragment>
        )}
      </div>
      <div className="modal-content-footer">
        <Button btnStyle="primary" onClick={handleConfirm}>
          Confirm
        </Button>
      </div>
    </div>
  );
};

AddressBookFilters.propTypes = propTypes;

export default AddressBookFilters;
